import type {
  MinimumBetSchema,
  PlayerTournamentProgressSchema,
  PrizeSchema,
  PublicCampaignSchema,
} from "@/types";
import type { MissionTask } from "../missions/types";

/**
 * The assembled Chase view — `campaigns` ⋈ `tournament_progress`.
 *
 * A chase is the "IM" (Individual Mission) tournament subtype: hit every target in as few spins
 * as you can; the prizes are a ladder keyed on a spin budget (`PrizeSchema.attempts`), and the
 * fewer spins the player needed, the bigger the rung they win. There is no leaderboard — the
 * ladder is the reward — which is why it is its own module and not a branch of the tournament
 * one. Composed from the generated schemas (CAMPAIGN-ARCHITECTURE.md, "Rely on the generated
 * backend types"); only the shaping the cards need is added on top.
 */
export interface Chase {
  campaign: PublicCampaignSchema;
  minimum_bet?: Pick<MinimumBetSchema, "value" | "currency"> &
    Partial<Pick<MinimumBetSchema, "display">>;
  /** The ladder, cheapest budget first. */
  rungs: ChaseRung[];
  /** The targets, in order. Shaped as mission task rows so `MissionTaskRow` renders them. */
  targets: ChaseTarget[];
  /** How many spins the player must have used before they may restart by hand. */
  spinsBeforeRestart: number | null;
  eligible_games: string[] | "ALL";
  eligible_games_count?: number;
  /** The player's own progress push. `null` before the first push: "we don't know yet" must not
   *  render as "0 spins", the way a Sprint Mission's missing pool must not read as "0 left". */
  progress: ChaseProgress | null;
}

/** One rung of the ladder: the prize and the spin budget it is won within. */
export interface ChaseRung {
  /** Content-derived, for React keys — budgets are unique on a ladder (validated server-side),
   *  the index is a tiebreaker for a misconfigured one. */
  id: string;
  /** `PrizeSchema.attempts` — the spins the player may use and still win this rung. */
  budget: number;
  prize: PrizeSchema;
  /** This player already won it (each rung pays once per player). */
  won: boolean;
}

/** A target with the player's live state — the mission task row shape, reused as is. */
export type ChaseTarget = MissionTask;

export type ChaseProgress = Pick<
  PlayerTournamentProgressSchema,
  "current" | "total" | "can_reset"
> & {
  /** The push carried per-target state (a backend that ships #4041's `tasks`). Without it the
   *  targets render without completion or meters, from the structural list alone. */
  hasTargetState: boolean;
};

/**
 * The one player-facing state machine every chase surface renders from.
 *
 * - `loading` — no progress push yet; render nothing confident.
 * - `chasing` — the run is on.
 * - `allWon` — every rung is this player's; nothing left to chase.
 * - `finished` — the campaign ended (`status "FI"`). Beats allWon.
 */
export type ChasePhase = "loading" | "chasing" | "allWon" | "finished";

export function chasePhase(chase: Chase): ChasePhase {
  if (chase.campaign.status === "FI") return "finished";
  if (chase.rungs.length > 0 && chase.rungs.every((rung) => rung.won)) {
    return "allWon";
  }
  if (chase.progress === null) return "loading";
  return "chasing";
}

/** Spins used in the current run, or `null` while the progress has not pushed. */
export function spinsUsed(chase: Chase): number | null {
  return chase.progress?.current ?? null;
}

/**
 * The run's whole budget: the hardest (largest-budget) rung the player has not won — the
 * backend's `total` — past which the run restarts. Falls back to the ladder itself when the
 * push carries no total (no prizes), and `null` when there is nothing to chase.
 */
export function spinBudget(chase: Chase): number | null {
  if (chase.progress?.total != null) return chase.progress.total;
  const unwon = chase.rungs.filter((rung) => !rung.won);
  return unwon.length > 0 ? unwon[unwon.length - 1].budget : null;
}

/**
 * The rung the player wins if they finish every target NOW: the cheapest unwon rung whose
 * budget still covers the spins used (`Tournament.process_mission`'s own rule, mirrored).
 * `null` while loading, when every rung is won, or when the spins already exceed every budget
 * (a run the backend is about to restart).
 */
export function inReachRung(chase: Chase): ChaseRung | null {
  const used = spinsUsed(chase);
  if (used === null) return null;
  return chase.rungs.find((rung) => !rung.won && rung.budget >= used) ?? null;
}

/** Spins the player can still use before the in-reach rung slips away. */
export function spinsLeftForRung(chase: Chase, rung: ChaseRung): number {
  return Math.max(0, rung.budget - (spinsUsed(chase) ?? 0));
}

export type ChaseUrgency = "calm" | "urgent";

/**
 * Frontend policy (like a Sprint Mission's `poolUrgency`): the counter warns when the run's
 * last chance is nearly spent — three spins or a fifth of the whole budget left — so the player
 * knows the next few spins decide whether they keep any prize at all this run.
 */
export const URGENT_SPINS_LEFT = 3;
export const URGENT_RATIO = 0.2;

export function chaseUrgency(chase: Chase): ChaseUrgency | null {
  const used = spinsUsed(chase);
  const budget = spinBudget(chase);
  if (used === null || budget === null || budget <= 0) return null;
  const left = budget - used;
  return left <= URGENT_SPINS_LEFT || left / budget <= URGENT_RATIO
    ? "urgent"
    : "calm";
}

/** Targets the player has hit this run. */
export function completedTargetCount(chase: Chase): number {
  return chase.targets.filter((target) => target.isCompleted).length;
}

/** Rungs this player has won. */
export function wonRungCount(chase: Chase): number {
  return chase.rungs.filter((rung) => rung.won).length;
}

/** The backend's ruling on the Restart button; `false` until the push says otherwise. */
export function canRestart(chase: Chase): boolean {
  return chase.progress?.can_reset === true;
}

/** The ladder's best prize — the cheapest rung — for the home card's hook. */
export function topRung(chase: Chase): ChaseRung | null {
  return chase.rungs[0] ?? null;
}
